import { useCallback } from "react";
import { z } from "zod";
import { Icon } from "@zapier/zinnia-icons";
import { currentAccountIdAtom } from "@zapier/canvas-state";
import { useAtomValue } from "jotai";

type IconName = React.ComponentProps<typeof Icon>["name"];

import { useCreateAppConnection } from "./useCreateAppConnection";
import styles from "./buildModeSuggestionsClient.module.css";
import { BuildModeSuggestionsParamsSchema } from "./index";
import { BuildModeSuggestionType } from "./types";
import { ErrorBoundary } from "./ErrorBoundary";
import React from "react";

export type BuildModeSuggestionsParams = z.infer<
  typeof BuildModeSuggestionsParamsSchema
>;

const promptMapping = {
  [BuildModeSuggestionType.SETUP_ZAPS]:
    "Help me configure field mapping for the Zaps on this canvas. Please work through them one at a time and ask for any additional information you need.",
  [BuildModeSuggestionType.LEAD_GENERATION]:
    "Build a lead generation system for me.",
  [BuildModeSuggestionType.CUSTOMER_SUPPORT]:
    "Build a customer support automation for me.",
  [BuildModeSuggestionType.ECOMMERCE_ORDER]:
    "Build an e-commerce order processing system for me.",
  [BuildModeSuggestionType.TOUR_MY_CANVAS]: "Use the tour tool",
  [BuildModeSuggestionType.EXPLAIN_MY_CANVAS]:
    "Explain what I have built on this canvas and how it works.",
  [BuildModeSuggestionType.WHAT_CAN_COPILOT_DO]:
    "What can Canvas Copilot do? Tell me about all the capabilities and features available.",
  [BuildModeSuggestionType.ZAPIER_COMMON_USE_CASES]:
    "What are common use cases for Zapier? Show me some popular automation examples and workflows.",
  [BuildModeSuggestionType.LEARN_ABOUT_CANVAS]:
    "Teach me about Canvas and how to use it effectively for building automations.",
  [BuildModeSuggestionType.ZAPIER_FOR_BEGINNERS]:
    "I'm new to Zapier. Can you teach me the basics and help me understand how to get started?",
};

const iconMapping: Record<BuildModeSuggestionType, IconName> = {
  [BuildModeSuggestionType.SETUP_ZAPS]: "miscCopilot",
  [BuildModeSuggestionType.LEAD_GENERATION]: "miscLearn",
  [BuildModeSuggestionType.CUSTOMER_SUPPORT]: "miscCopilot",
  [BuildModeSuggestionType.ECOMMERCE_ORDER]: "miscLearn",
  [BuildModeSuggestionType.TOUR_MY_CANVAS]: "navCanvas",
  [BuildModeSuggestionType.EXPLAIN_MY_CANVAS]: "navCanvas",
  [BuildModeSuggestionType.WHAT_CAN_COPILOT_DO]: "miscCopilot",
  [BuildModeSuggestionType.ZAPIER_COMMON_USE_CASES]: "miscBoltAltFill",
  [BuildModeSuggestionType.LEARN_ABOUT_CANVAS]: "navCanvas",
  [BuildModeSuggestionType.ZAPIER_FOR_BEGINNERS]: "miscLearn",
  [BuildModeSuggestionType.CREATE_AUTH]: "miscCopilot",
};

export function BuildModeSuggestions(props: {
  args: BuildModeSuggestionsParams;
}) {
  return (
    <ErrorBoundary>
      <BuildModeSuggestionsInner {...props} />
    </ErrorBoundary>
  );
}

export function BuildModeSuggestionsInner(props: {
  args: BuildModeSuggestionsParams;
}) {
  // Tool components should NOT use useCopilotChat - they're rendered outside the provider context
  // Instead, they should trigger actions that the tool system can respond to
  const accountId = useAtomValue(currentAccountIdAtom);
  const { createNewConnection } = useCreateAppConnection(Number(accountId));
  const args = props?.args || {};
  const { suggestions, selected_api } = args;

  const handleSuggestionClick = useCallback(
    async (suggestion: BuildModeSuggestionType) => {
      const promptText =
        promptMapping[suggestion as keyof typeof promptMapping] || suggestion;

      // Tool components can't directly control the chat - instead, trigger page navigation
      // or use window postMessage to communicate with the parent copilot
      window.postMessage(
        {
          type: "COPILOT_USER_MESSAGE",
          payload: {
            message: promptText,
            source: "build_mode_suggestions",
          },
        },
        "*",
      );
    },
    [],
  );

  const connectApp = useCallback(async () => {
    if (!selected_api) {
      return;
    }
    const res = await fetch(
      `/api/v4/implementations-meta/public?selected_apis=${selected_api}`,
    );
    const data = await res.json();
    const { id } = await createNewConnection(data.results[0]);
    return id;
  }, [selected_api, createNewConnection]);

  const handleButtonClick = useCallback(
    async (suggestion: BuildModeSuggestionType) => {
      // Check if this is an auth creation action
      if (suggestion === BuildModeSuggestionType.CREATE_AUTH) {
        if (!selected_api) {
          // Show error message if CREATE_AUTH is suggested without selected_api
          const errorMessage =
            "Cannot create auth connection: No API selected. The LLM must provide selected_api when suggesting CREATE_AUTH.";

          // Send error message via postMessage
          window.postMessage(
            {
              type: "COPILOT_USER_MESSAGE",
              payload: {
                message: errorMessage,
                source: "build_mode_suggestions_error",
              },
            },
            "*",
          );
          return;
        }

        const id = await connectApp();

        // Push a message to chat about the auth creation
        let authMessage: string = "";
        if (id) {
          authMessage = `Auth created successfully with ID: ${id}`;
        } else {
          authMessage = `Failed to create connection to ${selected_api}`;
        }

        // Send auth result message via postMessage
        window.postMessage(
          {
            type: "COPILOT_USER_MESSAGE",
            payload: {
              message: authMessage,
              source: "build_mode_suggestions_auth",
            },
          },
          "*",
        );
      } else {
        // Otherwise, treat it as a chat suggestion
        await handleSuggestionClick(suggestion);
      }
    },
    [connectApp, handleSuggestionClick, selected_api],
  );

  return (
    <div className={styles.container}>
      {suggestions?.map((suggestion) => {
        const iconName = iconMapping[suggestion as keyof typeof iconMapping];
        return (
          <button
            key={suggestion}
            onClick={() => handleButtonClick(suggestion)}
            className={styles.suggestionButton}
          >
            <Icon
              name={iconName}
              size="20"
              color="UiPrimaryStronger"
              className={styles.buttonIcon}
            />
            <span className={styles.buttonText}>{suggestion}</span>
          </button>
        );
      })}
    </div>
  );
}
